import { Injectable } from '@angular/core';
import { Observable, map, shareReplay, switchMap } from 'rxjs';
import { News } from '@models';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';
import { limit, orderBy } from '@angular/fire/firestore';

/** Noticias que pinta la app cuando no se pide otro tamaño (feature #118). */
const LATEST_DEFAULT_COUNT = 20;

/**
 * Margen que `latest()` pide de más a Firestore.
 *
 * `FirestoreService.listDocuments` descarta en **cliente** los documentos con
 * `deleted === true` —el borrado del panel es lógico: `FirestoreService.delete`
 * hace `update({ deleted: true })`— y aquí se descartan además los `active:
 * false` (R45: noticias ocultas en la app). Los dos filtros corren *después*
 * del `limit` del servidor, así que sin margen un `limit(20)` podría acabar
 * pintando menos de 20 tarjetas. El margen es el colchón para esos descartes y
 * solo se paga en lecturas cuando existen.
 */
const LATEST_FETCH_MARGIN = 10;

/**
 * Tope de seguridad del listado del admin.
 *
 * El panel **gestiona** las noticias, así que pide bastantes más que la app,
 * pero sin cargar el histórico entero: 200 cubre el volumen habitual de un
 * tenant (el sync incremental añade unas pocas al día) y acota la lectura.
 * Además, al ir con `orderBy('publishedAt', 'desc')`, el corte —cuando
 * llegue— es predecible: se quedan fuera las **más antiguas**, no un
 * subconjunto arbitrario como pasaba con el `limit` sin orden.
 */
const ADMIN_MAX_ITEMS = 200;

@Injectable({
  providedIn: 'root',
})
export class NewsService {
  /**
   * Listado del **admin**: todo, de la más reciente a la más antigua.
   *
   * El orden va en la query, no en cliente. Es seguro porque `publishedAt`
   * existe siempre: lo escribe el alta/edición del panel (`news.component.ts`)
   * y lo rellena el mapeo del sync de WordPress, así que el `orderBy` no
   * excluye ningún documento.
   *
   * La app NO debe consumir esto: para eso está `latest()`.
   */
  public all$: Observable<News[]>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<News>(
          this.tenantContext.collections.NEWS,
          orderBy('publishedAt', 'desc'),
          limit(ADMIN_MAX_ITEMS),
        ),
      ),
      shareReplay(1),
    );
  }

  /**
   * Últimas `count` noticias visibles, para la **app** (feature #118).
   *
   * Orden y recorte van server-side (`orderBy` + `limit`), que es lo que hace
   * real el ahorro de lecturas: un `limit` sin `orderBy` devuelve documentos
   * arbitrarios y ordenarlos después en cliente no garantiza que sean los más
   * recientes.
   *
   * `orderBy('publishedAt', 'desc')` sin `where` se resuelve con el índice de
   * campo único que Firestore mantiene solo: no requiere declarar ningún índice
   * compuesto en `apps/firebase/firestore.indexes.json`.
   */
  latest(count: number = LATEST_DEFAULT_COUNT): Observable<News[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<News>(
          this.tenantContext.collections.NEWS,
          orderBy('publishedAt', 'desc'),
          limit(count + LATEST_FETCH_MARGIN),
        ),
      ),
      // El filtro de `active` se queda en cliente a propósito: subirlo a la
      // query (`where('active','==',true)` + `orderBy`) exige índice compuesto
      // y los tenants enterprise viven en proyectos Firebase propios donde no
      // está garantizado que esté desplegado.
      map((items) => items.filter((item) => item.active).slice(0, count)),
      shareReplay(1),
    );
  }

  getById(id: string): Observable<News> {
    return this.firestore.getDocument<News>(this.tenantContext.collections.NEWS, id);
  }

  async create(data: Omit<News, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return this.firestore.create<News>(this.tenantContext.collections.NEWS, data);
  }

  async update(data: Partial<News> & { id: string }): Promise<void> {
    return this.firestore.update<News>(this.tenantContext.collections.NEWS, data);
  }

  /**
   * Muestra/oculta una noticia en la app sin borrarla (feature #118, R45).
   * Azúcar sobre `update`: es la única forma de "quitar de la app" una noticia
   * importada, ya que el siguiente sync recrearía el documento borrado (el id
   * es determinista).
   */
  async setActive(id: string, active: boolean): Promise<void> {
    return this.update({ id, active });
  }

  async delete(id: string): Promise<void> {
    return this.firestore.delete(this.tenantContext.collections.NEWS, id);
  }
}
